import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans, useLingui } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { Button } from '@startuptools/ui/Button';
import { Radio } from '@startuptools/ui/Radio';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { useMemo, useState } from 'react';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  GqlTransactionKind,
  GqlValuationMethod,
  GqlWarrantProgram,
  GqlWarrantProgramKind,
} from '../../../../../graphql/base-types.graphql';
import { Option } from '@startuptools/ui/Option';
import { Select } from '@startuptools/ui/Select';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { useStepValuationSuspenseQuery } from './StepValuation.graphql';
import { isNil, orderBy, toNumber } from 'lodash-es';
import { BigNumber } from 'bignumber.js';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { Box } from '@startuptools/ui/Box';
import { DateTime } from 'luxon';
import { DateInput } from '@startuptools/ui/custom/DateInput';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { useWpSetValuationMutation } from '../../../../../graphql/react-operations';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { DATE_FORMAT_LUXON } from '@startuptools/common/common';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';

type FormValues = {
  valuationMethod: GqlValuationMethod;
  selectedId: string | null;
  valuationDate: DateTime | null;
  valuationNumberOfShares: string;
  valuationCompanyValue: string;
  valuationPricePerShare: string;
};

enum ValuationKind {
  ShareIssue,
  ShareCapitalChange,
}

type SelectOptionElement = {
  id: string;
  kind: ValuationKind;
  name: string;
  pricePerShare: number;
  currency: string;
  value: number;
  date: string;
};

export const WpStepValuation = ({
  warrantProgram,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    | 'id'
    | 'companyId'
    | 'valuationSccId'
    | 'valuationShareIssueId'
    | 'valuationMethod'
    | 'valuationDate'
    | 'valuationCompanyValue'
    | 'valuationNumberOfShares'
    | 'warrantKind'
    | 'warrantStartDate'
  >;
}) => {
  const { i18n } = useLingui();
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  const {
    data: { shareIssues, completeSccs },
  } = useStepValuationSuspenseQuery({ variables: { companyId: warrantProgram.companyId } });

  const [doSetValuation, { loading }] = useWpSetValuationMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });

  const selectOptions = useMemo<SelectOptionElement[]>(() => {
    const sisElements: SelectOptionElement[] = shareIssues
      .map(si => {
        if (
          isNil(si.meetingConcern) ||
          isNil(si.pricePerShare) ||
          isNil(si.numberOfSharesBefore) ||
          isNil(si.maxNumberOfShares)
        ) {
          return undefined;
        }
        const value = new BigNumber(si.numberOfSharesBefore)
          .plus(si.maxNumberOfShares)
          .times(si.pricePerShare)
          .toNumber();
        return {
          id: si.id,
          kind: ValuationKind.ShareIssue,
          name: si.description,
          date: si.meetingConcern?.meeting?.startTime,
          pricePerShare: toNumber(si.pricePerShare), // this.localeId) + ` ${$localize`kr`}`,
          currency: si.currency || 'SEK',
          value,
        };
      })
      .filter(e => !isNil(e));

    const sccsElements: SelectOptionElement[] = completeSccs
      .filter(scc => scc.kind === GqlTransactionKind.Issue && ['NYEMISSION', 'NYBILDNING'].includes(scc.name))
      .map(scc => {
        return {
          id: scc.id,
          kind: ValuationKind.ShareCapitalChange,
          name: scc.caseNumber ?? '',
          date: scc.decisionDate ?? scc.registrationDate ?? '',
          pricePerShare: scc.pricePerShare!, // this.localeId) + ` ${$localize`kr`}`,
          currency: scc.currency ?? 'SEK',
          value: scc.postMoney!,
        };
      });

    return orderBy([...sisElements, ...sccsElements], e => e.date, 'desc');
  }, [shareIssues, completeSccs]);

  const { control, watch, setValue, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      selectedId: warrantProgram.valuationSccId ?? warrantProgram.valuationShareIssueId,
      valuationMethod: warrantProgram.valuationMethod ?? GqlValuationMethod.Issue,
      valuationDate: warrantProgram.valuationDate ? DateTime.fromISO(warrantProgram.valuationDate) : null,
      valuationCompanyValue: warrantProgram.valuationCompanyValue ?? '',
      valuationNumberOfShares: warrantProgram.valuationNumberOfShares ?? '',
      valuationPricePerShare:
        warrantProgram.valuationNumberOfShares && warrantProgram.valuationCompanyValue
          ? BigNumber(warrantProgram.valuationCompanyValue).div(warrantProgram.valuationNumberOfShares).toString()
          : '',
    },
  });

  const selectedValuationMethod = watch('valuationMethod');
  const valuationNumberOfShares = watch('valuationNumberOfShares');
  const valuationCompanyValue = watch('valuationCompanyValue');
  const valuationPricePerShare = watch('valuationPricePerShare');

  const onSubmit: SubmitHandler<FormValues> = async values => {
    if (values.valuationMethod === GqlValuationMethod.Issue) {
      const selected = selectOptions.find(o => o.id === values.selectedId)!;
      await doSetValuation({
        variables: {
          companyId: warrantProgram.companyId,
          input: {
            id: warrantProgram.id,
            valuationMethod: values.valuationMethod,
            valuationSccId: selected.kind === ValuationKind.ShareCapitalChange ? values.selectedId : null,
            valuationShareIssueId: selected.kind === ValuationKind.ShareIssue ? values.selectedId : null,
            valuationNumberOfShares: BigNumber(selected.value).div(selected.pricePerShare).toString(),
            valuationCompanyValue: selected.value.toString(),
            valuationDate: selected.date,
          },
        },
      });
    } else {
      await doSetValuation({
        variables: {
          companyId: warrantProgram.companyId,
          input: {
            id: warrantProgram.id,
            valuationMethod: values.valuationMethod,
            valuationSccId: null,
            valuationShareIssueId: null,
            valuationNumberOfShares: values.valuationNumberOfShares,
            valuationCompanyValue: values.valuationCompanyValue,
            valuationDate: values.valuationDate!.toUTC(0, { keepLocalTime: true }).toString(),
          },
        },
      });
    }
    return stepNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="The Company's valuation" />
        </Typography>
        <Alert
          endDecorator={
            <>
              {warrantProgram.warrantKind === GqlWarrantProgramKind.Employee && (
                <Button variant="outlined" onClick={() => setShowMoreInfo(!showMoreInfo)}>
                  <Trans id="More Info" />
                </Button>
              )}
            </>
          }
        >
          <Stack>
            {warrantProgram.warrantKind === GqlWarrantProgramKind.Employee ? (
              <Trans id="There are certain limits on how many qualified employee stock options may be issued, based on the value of the company." />
            ) : (
              <Trans id="The valuation influences how much the warrants will cost." />
            )}

            {warrantProgram.warrantKind === GqlWarrantProgramKind.Employee && showMoreInfo && (
              <>
                <Trans id="If the company within the last 12 months has done a new share issue at market level (not a share issue directed only to existing owners at a constructed low valuation) it is applicable. After 12 months the company is valued at the difference between assets and depts according to the balance sheet. In a startup the later calculation will almost always give a lower valuation. If you want to make a large option program it may then be worth waiting for those 12 months to expire. For the employee on the other hand the valuation doesn't matter, the options are worth the same (in the sense that you get the same amount) in both cases. We collect data from Bolagsverket, but there may be share issues which are not registered yet." />
              </>
            )}
          </Stack>
        </Alert>

        <Controller
          control={control}
          name="valuationMethod"
          rules={{ required: true }}
          render={({ field }) => (
            <RadioGroup {...field}>
              <Radio value={GqlValuationMethod.Issue} label={<Trans id="Use the share issue" />} />
              <Radio
                value={GqlValuationMethod.ManualIssue}
                label={
                  <Trans id="There is another share issue or sales transaction which has not yet been registered and which values the company to..." />
                }
              />
              <Radio value={GqlValuationMethod.Other} label={<Trans id="Use a different valuation" />} />
            </RadioGroup>
          )}
        />

        {selectedValuationMethod === GqlValuationMethod.Issue ? (
          <Controller
            control={control}
            name="selectedId"
            render={({ field: { onChange, ...field }, fieldState: { error } }) => (
              <FormControl required error={Boolean(error)}>
                <FormLabel>
                  <Trans id="Select a share issue" />
                </FormLabel>
                <Select {...field} onChange={(_, v) => onChange(v)} sx={{ maxWidth: 800 }} required>
                  {selectOptions.map(o => (
                    <Option value={o.id} key={o.id}>
                      <Typography component="span">
                        {o.name}, beslutsdatum: <DateFormat locale={i18n.locale}>{o.date}</DateFormat>,{' '}
                        <NumberFormat locale={i18n.locale} variant="currency" currency={o.currency}>
                          {o.pricePerShare}
                        </NumberFormat>{' '}
                        <Trans id="per share" />,{' '}
                        <NumberFormat locale={i18n.locale} variant="currency" currency={o.currency}>
                          {o.value}
                        </NumberFormat>
                      </Typography>
                    </Option>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        ) : (
          <>
            <Controller
              control={control}
              name="valuationDate"
              rules={{
                required: true,
                validate: value => {
                  if (!value || !warrantProgram.warrantStartDate) {
                    return true;
                  }
                  if (value.startOf('day') > DateTime.fromISO(warrantProgram.warrantStartDate).startOf('day')) {
                    return i18n._({
                      id: `Cannot be after option program's start date: {date}`,
                      values: { date: DateTime.fromISO(warrantProgram.warrantStartDate).toFormat(DATE_FORMAT_LUXON) },
                    });
                  }
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl sx={{ maxWidth: 400 }} required error={Boolean(error)}>
                  <FormLabel>
                    <Trans id="Date of Valuation" />
                  </FormLabel>
                  <DateInput {...field} />
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="valuationNumberOfShares"
              render={({ field: { onChange, ...field }, fieldState: { error } }) => (
                <FormControl sx={{ maxWidth: 400 }} required error={Boolean(error)}>
                  <FormLabel>
                    <Trans id="Number of Shares at valuation" />
                  </FormLabel>
                  <NumericFormatInput
                    {...field}
                    required
                    decimalScale={0}
                    onChange={ev => {
                      onChange(ev.target.value);
                      if (ev.target.value && valuationCompanyValue) {
                        const newPricePerShare = BigNumber(valuationCompanyValue).div(ev.target.value).toString();
                        if (newPricePerShare !== valuationPricePerShare) {
                          setValue('valuationPricePerShare', newPricePerShare);
                        }
                      }
                    }}
                  />
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="valuationCompanyValue"
              render={({ field: { value, onChange, ...field }, fieldState: { error } }) => (
                <FormControl sx={{ maxWidth: 400 }} required error={Boolean(error)}>
                  <FormLabel>
                    <Trans id="Company's Valuation" />
                  </FormLabel>
                  <NumericFormatInput
                    {...field}
                    required
                    value={value}
                    onChange={ev => {
                      onChange(ev.target.value);
                      if (ev.target.value && valuationCompanyValue) {
                        const newPricePerShare = BigNumber(ev.target.value).div(valuationNumberOfShares).toString();
                        if (newPricePerShare !== valuationPricePerShare) {
                          setValue('valuationPricePerShare', newPricePerShare);
                        }
                      }
                    }}
                  />
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="valuationPricePerShare"
              render={({ field: { value, onChange, ...field }, fieldState: { error } }) => (
                <FormControl sx={{ maxWidth: 400 }} required error={Boolean(error)}>
                  <FormLabel>
                    <Trans id="Price per Share" />
                  </FormLabel>
                  <NumericFormatInput
                    {...field}
                    required
                    value={value}
                    onChange={ev => {
                      onChange(ev.target.value);
                      if (ev.target.value && valuationCompanyValue) {
                        const newCompanyValue = BigNumber(valuationNumberOfShares).times(ev.target.value).toString();
                        if (newCompanyValue !== valuationCompanyValue) {
                          setValue('valuationCompanyValue', newCompanyValue);
                        }
                      }
                    }}
                  />
                </FormControl>
              )}
            />
          </>
        )}

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
